// ─── Single source of truth for a provider's price + unit ───
//
// The bug this fixes: Browse said "$100/hr" while the profile said "$100 per
// event" for the SAME provider, because every surface GUESSED the unit
// differently (one by a price threshold, one by whether package_price exists).
//
// The real unit is unambiguous in the data. During onboarding
// (ProviderOnboarding submit) the provider's first service sets EXACTLY one of:
//   • hourly_rate  (non-null)  → they are HOURLY        → "/hr" · "per hour"
//   • package_price(non-null)  → they are NOT hourly    → per event OR per person
// and the service's `price_type` ('hourly' | 'fixed' | 'per_person') /
// profile `price_unit` distinguishes event vs person. Checkout already reads
// `price_type` — this helper makes every display surface agree with it.
//
// It also supports two pricing MODES that carry no single fixed number:
//   • quote  → no price shown; the primary CTA becomes "Request a Quote"
//   • range  → provider set a min+max; cards show "$100–$500", the profile /
//              bookings use the low end (price_min).
//
// Returns: { amount, unit, short, long, suffixShort, suffixLong,
//            isQuote, isRange, rangeMin, rangeMax, display }
//   amount       – the number to show (range → rangeMin; quote → 0)
//   unit         – 'hour' | 'event' | 'person' | ''
//   short        – '/hr' | '/event' | '/person'
//   long         – 'per hour' | 'per event' | 'per person'
//   suffixShort  – same as short but '' when unknown (so we never show a wrong unit)
//   suffixLong   – same as long  but '' when unknown
//   isQuote      – true → quote-only, no numeric price
//   isRange      – true → min/max band present
//   rangeMin/Max – numbers | null
//   display      – ready-to-render string: 'Custom quote' | '$100–$500/event' | '$100/hr'

// Normalize any of the unit strings we store to 'hour' | 'event' | 'person'.
function normalizeUnit(raw) {
  const v = String(raw || '').toLowerCase();
  if (v === 'hourly' || v === 'hour' || v === 'hr' || v === 'hourly_rate') return 'hour';
  if (v === 'per_person' || v === 'person' || v === 'per person') return 'person';
  if (v === 'fixed' || v === 'flat' || v === 'event' || v === 'per_event' || v === 'per event') return 'event';
  return '';
}

// Pricing MODE is orthogonal to unit: 'quote' | 'range' | '' (a normal priced row).
function normalizePriceMode(raw) {
  const v = String(raw || '').toLowerCase();
  if (v === 'quote') return 'quote';
  if (v === 'range') return 'range';
  return '';
}

// Format a whole-dollar number as "$1,200" (no cents, thousands grouped).
function fmt(n) {
  const v = Number(n) || 0;
  return `$${v.toLocaleString('en-CA', { maximumFractionDigits: 0 })}`;
}

export function providerPrice(p) {
  if (!p) return {
    amount: 0, unit: '', short: '', long: '', suffixShort: '', suffixLong: '',
    isQuote: false, isRange: false, rangeMin: null, rangeMax: null, display: '',
  };

  const svc = (p.services && p.services[0]) || null;

  // Mode: profile-level wins, else first-service. Quote beats range.
  const mode = normalizePriceMode(p.price_type) || normalizePriceMode(svc && svc.price_type);

  // Range bounds: prefer profile fields, fall back to first-service fields.
  const rawMin = p.price_min != null ? p.price_min : (svc ? svc.price_min : null);
  const rawMax = p.price_max != null ? p.price_max : (svc ? svc.price_max : null);
  const min = Number(rawMin);
  const max = Number(rawMax);
  const hasRange = rawMin != null && rawMax != null &&
    !Number.isNaN(min) && !Number.isNaN(max) && min > 0 && max >= min;

  // ─── Mode 1: QUOTE-ONLY — no number anywhere. ───
  if (mode === 'quote') {
    return {
      amount: 0, unit: '', short: '', long: '', suffixShort: '', suffixLong: '',
      isQuote: true, isRange: false, rangeMin: null, rangeMax: null,
      display: 'Custom quote',
    };
  }

  // ─── Mode 2: RANGE — band on cards, low end for bookings. ───
  if (mode === 'range' && hasRange) {
    // A range still carries a unit (e.g. "$100–$500/event"). The service's own
    // price_type is 'range', so the true unit is on price_unit if set, else event.
    const runit = normalizeUnit(p.price_unit) || 'event';
    const shortMap = { hour: '/hr', event: '/event', person: '/person' };
    const longMap = { hour: 'per hour', event: 'per event', person: 'per person' };
    const short = shortMap[runit] || '';
    const long = longMap[runit] || '';
    const display = max === min ? `${fmt(min)}${short}` : `${fmt(min)}–${fmt(max)}${short}`;
    return {
      amount: min,                 // legacy callers show the low end, never 0/NaN
      unit: runit,
      short: short || '/event',
      long: long || 'per event',
      suffixShort: short,
      suffixLong: long,
      isQuote: false, isRange: true, rangeMin: min, rangeMax: max,
      display,
    };
  }

  // ─── Legacy priced modes (UNCHANGED behavior) ───
  const hourly = Number(p.hourly_rate);
  const pkg = Number(p.package_price);
  const hasHourly = p.hourly_rate != null && !Number.isNaN(hourly) && hourly > 0;
  const hasPkg = p.package_price != null && !Number.isNaN(pkg) && pkg > 0;
  const svcType = normalizeUnit(svc && svc.price_type);

  let unit = '';
  let amount = 0;

  if (hasHourly) {
    // hourly_rate set → definitively hourly.
    unit = 'hour';
    amount = hourly;
  } else if (hasPkg) {
    // package_price set → per event, unless the service/profile says per person.
    unit = svcType === 'person' || normalizeUnit(p.price_unit) === 'person' ? 'person' : 'event';
    amount = pkg;
  } else if (svc && svc.base_price != null) {
    // Fall back to the service record when profile fields are empty.
    unit = svcType || 'event';
    amount = Number(svc.base_price) || 0;
  } else {
    // Nothing set — show the number if any, but no (potentially wrong) unit.
    amount = hourly || pkg || 0;
    unit = svcType; // may be ''
  }

  const shortMap = { hour: '/hr', event: '/event', person: '/person' };
  const longMap = { hour: 'per hour', event: 'per event', person: 'per person' };
  const short = shortMap[unit] || '';
  const long = longMap[unit] || '';

  return {
    amount,
    unit,
    short: short || '/hr',        // legacy default when a surface must show something
    long: long || 'per event',    // legacy default
    suffixShort: short,           // '' when unknown → surface can omit it
    suffixLong: long,
    isQuote: false, isRange: false, rangeMin: null, rangeMax: null,
    display: `${fmt(amount)}${short}`,
  };
}
